import { Event } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { ServiceBooking } from '@mirai/services';
import { Button, Modal, styles, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { RoomCard } from './Booking.RoomCard';
import { useSelectedRoomData } from './hooks';
import { prepareRoom } from '../../../helpers/prepareRoom';
import { Occupation } from '../../__shared__';
import { toParties } from '../../__shared__/Occupation/helpers/toParties';
import { EVENT } from '../../helpers';
import { L10N } from '../Booking.l10n';
import * as style from '../Booking.module.css';
import { getOccupationDataSource } from '../helpers';

const ModalOccupation = ({
  dataSource: { details = [], id, modify: { occupation: { rooms = [] } = {} } = {} } = {},
  room,
  token,
  visible,
  onBack = () => {},
  onClose = () => {},
  onConfirm = () => {},
  ...others
}) => {
  const { isMobile } = useDevice();

  const { translate } = useLocale();

  const {
    configDataSource,
    selectedRoom: selectedOccupation,
    selectedRoomData: { image, occupation, plan, rate, title } = {},
    showSelectedRoom,
  } = useSelectedRoomData({ details, room, rooms });

  const [busy, setBusy] = useState(false);
  const [occupationDataSource, setOccupationDataSource] = useState(
    getOccupationDataSource({ configDataSource, occupation: selectedOccupation ? [selectedOccupation] : [] }),
  );
  const [value, setValue] = useState();

  const visiblePrevious = useRef(false);

  useEffect(() => {
    const isVisible = visible === true;
    const isOpening = isVisible && !visiblePrevious.current;

    visiblePrevious.current = isVisible;
    if (!isOpening) return;

    setValue([...Array(1)].map(() => prepareRoom(configDataSource)));
    setOccupationDataSource(
      getOccupationDataSource({ configDataSource, occupation: selectedOccupation ? [selectedOccupation] : [] }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configDataSource, visible, selectedOccupation]);

  const handleSubmit = async () => {
    setBusy(true);
    const party = toParties(value) || [];
    const requestQuote = ServiceBooking.modification.occupation.quote;
    const roomSelected = room !== undefined && room !== null ? { room } : undefined;
    const response = await requestQuote({
      id,
      party: party[0],
      token,
      ...(roomSelected ? { roomId: room?.id } : undefined),
    })
      .catch(() => {
        Event.publish(EVENT.NOTIFICATION, {
          warning: true,
          defaultMessage: translate(L10N.NOTIFICATION_WARNING_NO_OCCUPANCY_AVAILABILITY),
        });
      })
      .finally(() => {
        setBusy(false);
      });

    if (!response) return;

    onClose();
    onConfirm({ ...response, party, ...roomSelected });
  };

  const showBack = isMobile || showSelectedRoom;

  return (
    <Modal
      {...others}
      fit
      title={translate(L10N.ACTION_EDIT_OCCUPATION)}
      visible={visible}
      className={styles(style.modalOccupation, others.className)}
      onClose={onClose}
    >
      <View>
        {showSelectedRoom && (
          <RoomCard
            {...{ image, occupation, plan, rate, title }}
            className={style.selectedRoomCard}
            testId="booking-edit-occupation-room-card"
          />
        )}
        <Occupation
          allowRooms={false}
          dataSource={occupationDataSource}
          value={value}
          onChange={setValue}
          testId="booking-edit-occupation"
        />
        <View row className={style.footerActions}>
          {showBack && (
            <Button secondary onPress={onBack} testId="booking-edit-occupation-back">
              {translate(L10N.ACTION_BACK)}
            </Button>
          )}
          <Button busy={busy} onPress={handleSubmit} testId="booking-edit-occupation-search">
            {translate(L10N.ACTION_SEARCH)}
          </Button>
        </View>
      </View>
    </Modal>
  );
};

ModalOccupation.displayName = 'Mirai:Core:Booking:ModalOccupation';

ModalOccupation.propTypes = {
  dataSource: PropTypes.shape({
    details: PropTypes.arrayOf(PropTypes.shape({})),
    id: PropTypes.string,
    modify: PropTypes.shape({
      occupation: PropTypes.shape({
        rooms: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            maxOccupation: PropTypes.shape({
              adults: PropTypes.number,
              children: PropTypes.number,
            }),
            minOccupation: PropTypes.shape({
              adults: PropTypes.number,
              children: PropTypes.number,
            }),
          }),
        ),
      }),
    }),
    price: PropTypes.shape({
      value: PropTypes.number,
    }),
  }),
  room: PropTypes.shape({
    id: PropTypes.string,
    index: PropTypes.number,
  }),
  token: PropTypes.string,
  visible: PropTypes.bool,
  onBack: PropTypes.func,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

export { ModalOccupation };
